import React, { useState, useEffect } from 'react';
import { 
  Chart as ChartJS, 
  CategoryScale, 
  LinearScale, 
  PointElement, 
  LineElement, 
  BarElement, 
  ArcElement,
  Title, 
  Tooltip, 
  Legend,
  Filler
} from 'chart.js';
import { Line, Bar, Doughnut, Pie } from 'react-chartjs-2';
import { 
  CalendarIcon, 
  CurrencyDollarIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  ArrowTrendingUpIcon,
  MapPinIcon,
  CarIcon
} from '@heroicons/react/24/outline';
import { getFirestore, collection, getDocs, query, orderBy, where } from 'firebase/firestore';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const ReservationAnalytics = () => {
  const [reservationData, setReservationData] = useState({
    reservations: [],
    analytics: {
      growth: { labels: [], datasets: [] },
      byStatus: { labels: [], datasets: [] },
      byCity: { labels: [], datasets: [] },
      revenue: { labels: [], datasets: [] },
      vehicleTypes: { labels: [], datasets: [] },
      durationAnalysis: { labels: [], datasets: [] }
    },
    kpis: {
      totalReservations: 0,
      confirmedReservations: 0,
      cancelledReservations: 0,
      totalRevenue: 0,
      averageRevenue: 0,
      refundedReservations: 0,
      activeReservations: 0
    }
  });
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    dateRange: '30',
    status: '',
    city: ''
  });
  const [cities, setCities] = useState([]);

  useEffect(() => {
    loadReservationData();
    loadCities();
  }, [filters]);

  const loadCities = async () => {
    try {
      const db = getFirestore();
      const reservationsRef = collection(db, 'reservations');
      const querySnapshot = await getDocs(reservationsRef);
      
      // Extract unique cities from spaceData.city
      const citySet = new Set();
      querySnapshot.docs.forEach(doc => {
        const reservation = doc.data();
        if (reservation.spaceData?.city && reservation.spaceData.city.trim()) {
          citySet.add(reservation.spaceData.city.trim());
        }
      });
      
      const uniqueCities = Array.from(citySet).sort();
      setCities(uniqueCities);
    } catch (error) {
      console.error('Error loading cities:', error);
    }
  };

  const loadReservationData = async () => {
    setLoading(true);
    try {
      const db = getFirestore();
      const reservationsRef = collection(db, 'reservations');
      
      // Get all reservations first, then filter in memory
      const querySnapshot = await getDocs(reservationsRef);
      let reservations = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Apply filters
      if (filters.status) {
        reservations = reservations.filter(reservation => reservation.state === filters.status);
      }
      
      if (filters.city) {
        reservations = reservations.filter(reservation => {
          const city = reservation.spaceData?.city || '';
          return city.toLowerCase().includes(filters.city.toLowerCase());
        });
      }

      const processedData = processReservationAnalytics(reservations, filters.dateRange);
      setReservationData(processedData);
    } catch (error) {
      console.error('Error loading reservation data:', error);
    } finally {
      setLoading(false);
    }
  };

  const processReservationAnalytics = (reservations, dateRange) => {
    let filteredReservations = reservations;
    let dateLabels = [];
    
    if (dateRange !== 'all') {
      const days = parseInt(dateRange);
      const endDate = new Date();
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - days);

      // Filter reservations by date range
      filteredReservations = reservations.filter(reservation => {
        const reservationDate = new Date(reservation.createTime?.toDate?.() || reservation.createTime);
        return reservationDate >= startDate && reservationDate <= endDate;
      });

      // Generate date labels
      for (let i = days - 1; i >= 0; i--) {
        const date = new Date();
        date.setDate(date.getDate() - i);
        dateLabels.push(date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }));
      }
    } else {
      // For "all time", use all reservations and generate labels for the last 30 days for the chart
      const endDate = new Date();
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - 30);
      
      for (let i = 29; i >= 0; i--) {
        const date = new Date();
        date.setDate(date.getDate() - i);
        dateLabels.push(date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }));
      }
    }

    // Process KPIs
    const kpis = {
      totalReservations: filteredReservations.length,
      confirmedReservations: filteredReservations.filter(r => r.state === 'confirmed').length,
      cancelledReservations: filteredReservations.filter(r => r.state === 'canceled').length,
      totalRevenue: filteredReservations.reduce((sum, r) => sum + (parseFloat(r.total) || 0), 0),
      refundedReservations: filteredReservations.filter(r => r.REFUNDISSUED === true).length,
      activeReservations: filteredReservations.filter(r => r.state === 'confirmed').length
    };
    kpis.averageRevenue = kpis.totalReservations > 0 ? kpis.totalRevenue / kpis.totalReservations : 0;

    // Process status distribution
    const statusCounts = {};
    filteredReservations.forEach(reservation => {
      const status = reservation.state || 'unknown';
      statusCounts[status] = (statusCounts[status] || 0) + 1;
    });

    // Process city distribution
    const cityCounts = {};
    filteredReservations.forEach(reservation => {
      const city = reservation.spaceData?.city || 'Unknown';
      cityCounts[city] = (cityCounts[city] || 0) + 1;
    });

    // Process vehicle type distribution
    const vehicleTypeCounts = {};
    filteredReservations.forEach(reservation => {
      const vehicleType = reservation.vehicleData?.make || 'Unknown';
      vehicleTypeCounts[vehicleType] = (vehicleTypeCounts[vehicleType] || 0) + 1;
    });

    // Process duration analysis
    const durationCounts = {};
    filteredReservations.forEach(reservation => {
      const duration = reservation.duration || 0;
      const unit = reservation.durationunit || 'hours';
      const key = `${duration} ${unit}`;
      durationCounts[key] = (durationCounts[key] || 0) + 1;
    });

    // Process growth data
    const reservationsByDate = {};
    const revenueByDate = {};
    
    filteredReservations.forEach(reservation => {
      const date = new Date(reservation.createTime?.toDate?.() || reservation.createTime);
      const dateKey = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      
      reservationsByDate[dateKey] = (reservationsByDate[dateKey] || 0) + 1;
      revenueByDate[dateKey] = (revenueByDate[dateKey] || 0) + (parseFloat(reservation.total) || 0);
    });

    // Generate chart datasets
    const growthData = {
      labels: dateLabels,
      datasets: [
        {
          label: 'New Reservations',
          data: dateLabels.map(label => reservationsByDate[label] || 0),
          borderColor: 'rgb(59, 130, 246)',
          backgroundColor: 'rgba(59, 130, 246, 0.1)',
          fill: true,
          tension: 0.4
        }
      ]
    };

    const statusData = {
      labels: Object.keys(statusCounts),
      datasets: [{
        data: Object.values(statusCounts),
        backgroundColor: [
          'rgba(34, 197, 94, 0.8)',
          'rgba(239, 68, 68, 0.8)',
          'rgba(59, 130, 246, 0.8)',
          'rgba(245, 158, 11, 0.8)'
        ],
        borderWidth: 2,
        borderColor: '#fff'
      }]
    };

    const cityData = {
      labels: Object.keys(cityCounts).slice(0, 10), // Top 10 cities
      datasets: [{
        label: 'Reservations',
        data: Object.values(cityCounts).slice(0, 10),
        backgroundColor: 'rgba(59, 130, 246, 0.8)',
        borderColor: 'rgb(59, 130, 246)',
        borderWidth: 1
      }]
    };

    const revenueData = {
      labels: dateLabels,
      datasets: [{
        label: 'Daily Revenue',
        data: dateLabels.map(label => revenueByDate[label] || 0),
        borderColor: 'rgb(34, 197, 94)',
        backgroundColor: 'rgba(34, 197, 94, 0.1)',
        fill: true,
        tension: 0.4
      }]
    };

    const vehicleData = {
      labels: Object.keys(vehicleTypeCounts).slice(0, 8), // Top 8 vehicle types
      datasets: [{
        data: Object.values(vehicleTypeCounts).slice(0, 8),
        backgroundColor: [
          'rgba(59, 130, 246, 0.8)',
          'rgba(16, 185, 129, 0.8)',
          'rgba(245, 158, 11, 0.8)',
          'rgba(239, 68, 68, 0.8)',
          'rgba(139, 92, 246, 0.8)',
          'rgba(236, 72, 153, 0.8)',
          'rgba(6, 182, 212, 0.8)',
          'rgba(34, 197, 94, 0.8)'
        ],
        borderWidth: 2,
        borderColor: '#fff'
      }]
    };

    const durationData = {
      labels: Object.keys(durationCounts).slice(0, 6), // Top 6 durations
      datasets: [{
        label: 'Reservations',
        data: Object.values(durationCounts).slice(0, 6),
        backgroundColor: 'rgba(139, 92, 246, 0.8)',
        borderColor: 'rgb(139, 92, 246)',
        borderWidth: 1
      }]
    };

    return {
      reservations: filteredReservations,
      analytics: {
        growth: growthData,
        byStatus: statusData,
        byCity: cityData,
        revenue: revenueData,
        vehicleTypes: vehicleData,
        durationAnalysis: durationData
      },
      kpis
    };
  };

  const KPICard = ({ title, value, icon: Icon, color, subtitle }) => (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-600">{title}</p>
          <p className="text-2xl font-bold text-gray-900">{value}</p>
          {subtitle && <p className="text-xs text-gray-500 mt-1">{subtitle}</p>}
        </div>
        <div className={`p-3 rounded-lg ${color}`}>
          <Icon className="w-6 h-6 text-white" />
        </div>
      </div>
    </div>
  );

  const ChartCard = ({ title, children, className = "" }) => (
    <div className={`bg-white rounded-lg shadow-sm border border-gray-200 p-6 ${className}`}>
      <h3 className="text-lg font-semibold text-gray-900 mb-4">{title}</h3>
      {children}
    </div>
  );

  if (loading && reservationData.reservations.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Reservation Analytics</h1>
          <p className="text-gray-600">Comprehensive insights into booking patterns, revenue, and trends</p>
        </div>
        
        {/* Filters */}
        <div className="flex items-center space-x-4 mt-4 sm:mt-0">
          <div className="flex items-center space-x-2">
            <CalendarIcon className="w-5 h-5 text-gray-500" />
            <select
              value={filters.dateRange}
              onChange={(e) => setFilters(prev => ({ ...prev, dateRange: e.target.value }))}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
            >
              <option value="all">All time</option>
              <option value="1">Today</option>
              <option value="2">Last 2 days</option>
              <option value="7">Last 7 days</option>
              <option value="14">Last 14 days</option>
              <option value="30">Last 30 days</option>
              <option value="60">Last 60 days</option>
              <option value="90">Last 90 days</option>
              <option value="180">Last 6 months</option>
              <option value="365">Last year</option>
            </select>
          </div>
          
          <select
            value={filters.status}
            onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
          >
            <option value="">All Statuses</option>
            <option value="confirmed">Confirmed</option>
            <option value="canceled">Cancelled</option>
            <option value="pending">Pending</option>
          </select>
          
          <select
            value={filters.city}
            onChange={(e) => setFilters(prev => ({ ...prev, city: e.target.value }))}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
          >
            <option value="">All Cities</option>
            {cities.map(city => (
              <option key={city} value={city}>{city}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Row 1: Reservation KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <KPICard
          title="Total Reservations"
          value={reservationData.kpis.totalReservations.toLocaleString()}
          icon={CalendarIcon}
          color="bg-blue-500"
          subtitle="In selected period"
        />
        <KPICard
          title="Confirmed"
          value={reservationData.kpis.confirmedReservations.toLocaleString()}
          icon={CheckCircleIcon}
          color="bg-green-500"
          subtitle="Active bookings"
        />
        <KPICard
          title="Total Revenue"
          value={`₹${reservationData.kpis.totalRevenue.toLocaleString()}`}
          icon={CurrencyDollarIcon}
          color="bg-purple-500"
          subtitle="From all reservations"
        />
        <KPICard
          title="Average Revenue"
          value={`₹${reservationData.kpis.averageRevenue.toFixed(2)}`}
          icon={ArrowTrendingUpIcon}
          color="bg-indigo-500"
          subtitle="Per reservation"
        />
      </div>

      {/* Row 2: Additional KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <KPICard
          title="Cancelled"
          value={reservationData.kpis.cancelledReservations.toLocaleString()}
          icon={XCircleIcon}
          color="bg-red-500"
          subtitle="Cancelled bookings"
        />
        <KPICard
          title="Refunded"
          value={reservationData.kpis.refundedReservations.toLocaleString()}
          icon={CurrencyDollarIcon}
          color="bg-orange-500"
          subtitle="Refunds issued"
        />
        <KPICard
          title="Active"
          value={reservationData.kpis.activeReservations.toLocaleString()}
          icon={ClockIcon}
          color="bg-green-500"
          subtitle="Currently active"
        />
      </div>

      {/* Row 3: Growth and Status Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Reservation Growth Chart */}
        <ChartCard title="Reservation Growth Trend">
          <Line
            data={reservationData.analytics.growth}
            options={{
              responsive: true,
              interaction: {
                mode: 'index',
                intersect: false,
              },
              plugins: {
                legend: {
                  position: 'top',
                },
              },
              scales: {
                y: {
                  beginAtZero: true,
                  title: {
                    display: true,
                    text: 'New Reservations'
                  }
                }
              },
            }}
          />
        </ChartCard>

        {/* Status Distribution */}
        <ChartCard title="Reservation Status Distribution">
          <Doughnut
            data={reservationData.analytics.byStatus}
            options={{
              responsive: true,
              plugins: {
                legend: {
                  position: 'bottom',
                },
              },
            }}
          />
        </ChartCard>
      </div>

      {/* Row 4: Revenue and Location */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Revenue Trend */}
        <ChartCard title="Daily Revenue Trend">
          <Line
            data={reservationData.analytics.revenue}
            options={{
              responsive: true,
              interaction: {
                mode: 'index',
                intersect: false,
              },
              plugins: {
                legend: {
                  position: 'top',
                },
              },
              scales: {
                y: {
                  beginAtZero: true,
                  title: {
                    display: true,
                    text: 'Revenue (₹)'
                  }
                }
              },
            }}
          />
        </ChartCard>

        {/* Top Cities */}
        <ChartCard title="Top Cities by Reservations">
          <Bar
            data={reservationData.analytics.byCity}
            options={{
              responsive: true,
              plugins: {
                legend: {
                  display: false,
                },
              },
              scales: {
                y: {
                  beginAtZero: true,
                },
              },
            }}
          />
        </ChartCard>
      </div>

      {/* Row 5: Vehicle Types and Duration Analysis */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Vehicle Types */}
        <ChartCard title="Popular Vehicle Types">
          <Pie
            data={reservationData.analytics.vehicleTypes}
            options={{
              responsive: true,
              plugins: {
                legend: {
                  position: 'bottom',
                },
              },
            }}
          />
        </ChartCard>

        {/* Duration Analysis */}
        <ChartCard title="Reservation Duration Analysis">
          <Bar
            data={reservationData.analytics.durationAnalysis}
            options={{
              responsive: true,
              plugins: {
                legend: {
                  display: false,
                },
              },
              scales: {
                y: {
                  beginAtZero: true,
                },
              },
            }}
          />
        </ChartCard>
      </div>
    </div>
  );
};

export default ReservationAnalytics; 